<template>
    <!-- Create Touchpoint Modal -->
    <div class="modal fade" id="createTouchpointModal" tabindex="-1" aria-labelledby="createTouchpointModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="createTouchpointModalLabel">Create New Touchpoint</h5>
                    <button type="button" class="btn btn-white float-end close-modal" data-bs-dismiss="modal" aria-label="Close" data-bs-toggle="tooltip" title="Close">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                    </button>
                </div>
                <div class="modal-body">
                    <form @submit.prevent="handleCreate">
                        <div class="mb-3">
                            <label for="createTouchpointName" class="form-label">Name</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                id="createTouchpointName" 
                                v-model="localName" 
                                :class="{ 'is-invalid': errors.name }"
                             aria-label="Name">
                            <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
                        </div>
                        <div class="mb-3">
                            <label for="createTouchpointDescription" class="form-label">Description</label>
                            <textarea 
                                class="form-control" 
                                id="createTouchpointDescription" 
                                v-model="localDescription" 
                                rows="3"
                            ></textarea>
                        </div>
                        <div class="mb-3">
                            <label for="createTouchpointAssetType" class="form-label">Channel</label>
                            <accordion-select
                                v-model="selectedAssetTypeOption"
                                :options="assetTypeOptions"
                                :if_allow_edit="!busy"
                                name="asset-type"
                                :class="{ 'is-invalid': errors.asset_type }"
                                @update:modelValue="handleAssetTypeChange"
                            />
                            <div v-if="errors.asset_type" class="invalid-feedback">{{ errors.asset_type }}</div>
                        </div>
                        <div class="mb-3">
                            <label for="createTouchpointDestination" class="form-label">Destination</label>
                            <accordion-select
                                v-model="selectedDestinationOption"
                                :options="destinationOptions"
                                :if_allow_edit="!busy"
                                :splitText="false"
                                name="destination"
                                @update:modelValue="handleDestinationChange"
                            />
                        </div>
                        <div class="mb-3">
                            <label for="createTouchpointLaunchDate" class="form-label">Launch Date & Time</label>
                            <DatetimePickerLocal 
                                v-model="localLaunchDate"
                                placeholder="Select launch date and time"
                                input-class="form-control"
                                :input-format="'MM/dd/yyyy hh:mm a'"
                                @update:modelValue="handleLaunchDateChange"
                            />
                        </div>
                    </form>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                    <button v-if="canEdit('playbooks')" type="button" class="btn btn-primary" @click="handleCreate" :disabled="busy">
                        {{ busy ? 'Creating...' : 'Create Touchpoint' }}
                    </button>
                    <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                        <button type="button" class="btn btn-primary" :disabled="true">
                            Create Touchpoint
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import permissionsMixin from '../../mixins/permissions';
import AccordionSelect from '../components/AccordionSelect.vue';
import DatetimePickerLocal from '../components/DatetimePickerLocal.vue';
import { usePlaybookStore } from '../../stores/playbook';
import { useNotifications } from '../../composables/useNotifications';

export default defineComponent({
    name: 'CreateTouchpointModal',
    mixins: [permissionsMixin],
    components: {
        AccordionSelect,
        DatetimePickerLocal
    },
    setup() {
        const playbookStore = usePlaybookStore();
        const { showNotification } = useNotifications();
        return {
            playbookStore,
            showNotification
        };
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        assetTypes: {
            type: Array,
            default: () => []
        },
        destinationOptions: {
            type: Array,
            default: () => []
        },
        client: {
            type: Object,
            default: () => ({})
        },
        playbook: {
            type: Object,
            default: null
        }
    },
    emits: ['create-touchpoint', 'close'],
    data() {
        return {
            localName: '',
            localDescription: '',
            localLaunchDate: null,
            selectedAssetTypeOption: null,
            selectedDestinationOption: null,
            selectedAssetType: null,
            destinationAssetId: null,
            busy: false,
            errors: {}
        };
    },
    computed: {
        assetTypeOptions() {
            return this.assetTypes.map(at => ({
                value: at.id,
                text: at.name,
                category: ''
            }));
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.showModal();
                });
            } else {
                this.hideModal();
            }
        }
    },
    methods: {
        showModal() {
            const modalElement = document.getElementById('createTouchpointModal');
            if (modalElement) {
                const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                if (existingModal) {
                    existingModal.dispose();
                }
                
                const modal = new window.bootstrap.Modal(modalElement);
                modal.show();
                
                // Listen for modal close events
                const handleHidden = () => {
                    this.resetForm();
                    this.$emit('close');
                    modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                };
                modalElement.addEventListener('hidden.bs.modal', handleHidden, { once: true });
            }
        },
        
        hideModal() {
            const modalElement = document.getElementById('createTouchpointModal');
            if (modalElement) {
                const modal = window.bootstrap.Modal.getInstance(modalElement);
                if (modal) {
                    modal.hide();
                }
            }
        },
        
        resetForm() {
            this.localName = '';
            this.localDescription = '';
            this.localLaunchDate = null;
            this.selectedAssetTypeOption = null;
            this.selectedDestinationOption = null;
            this.selectedAssetType = null;
            this.destinationAssetId = null;
            this.errors = {};
            this.busy = false;
        },
        
        handleAssetTypeChange(option) {
            if (!option) {
                this.selectedAssetType = null;
                this.errors = {};
                return;
            }
            const assetType = this.assetTypes.find(a => a.id === option.value);
            if (assetType) {
                this.selectedAssetType = assetType;
                this.errors = {};
            }
        },
        
        handleDestinationChange(option) {
            if (!option) {
                this.destinationAssetId = null;
                this.selectedDestinationOption = null;
                return;
            }
            this.destinationAssetId = option.value;
        },
        
        handleLaunchDateChange(newDate) {
            // DatetimePickerLocal emits a Date object in UTC
            this.localLaunchDate = newDate;
        },
        
        handleCreate() {
            if (this.busy) return;
            
            // Validation
            this.errors = {};
            if (!this.localName.trim()) {
                this.errors.name = 'Name is required';
            }
            if (!this.selectedAssetType) {
                this.errors.asset_type = 'Channel is required';
            }
            
            if (Object.keys(this.errors).length > 0) {
                return;
            }
            
            this.busy = true;
            
            const payload = {
                client_id: this.client.id,
                playbook_id: this.playbook.id,
                name: this.localName.trim(),
                description: this.localDescription.trim() || null,
                playbook_asset_type_id: this.selectedAssetType.id,
                status: 'draft',
                inputs: []
            };
            
            // Add optional fields if they exist
            if (this.localLaunchDate) {
                // DatetimePickerLocal emits UTC Date object, convert to ISO string
                payload.launch_date = this.localLaunchDate instanceof Date 
                    ? this.localLaunchDate.toISOString() 
                    : this.localLaunchDate;
            }
            
            if (this.destinationAssetId) {
                payload.destination_asset_id = this.destinationAssetId;
            }
            
            window.axios.post(`/api/playbooks/${this.client.url}/assets`, payload)
                .then(response => {
                    // Close modal
                    this.hideModal();
                    
                    // Show success message
                    const touchpointName = response.data?.asset?.name || this.localName;
                    this.showNotification({
                        text: '<strong>' + touchpointName + '</strong> was created!'
                    });
                    
                    // Emit the created asset to parent
                    if (response.data && response.data.asset) {
                        this.$emit('create-touchpoint', response.data.asset);
                    }
                })
                .catch(error => {
                    this.showNotification({
                        text: 'Failed to create touchpoint. ' + (error.response?.data?.message || 'Please try again.'),
                        type: 'error'
                        });
                })
                .finally(() => {
                    this.busy = false;
                });
        },
        
        setupModalEventListeners() {
            const modalElement = document.getElementById('createTouchpointModal');
            if (modalElement) {
                // Initialize tooltips when modal is shown
                modalElement.addEventListener('shown.bs.modal', () => {
                    this.$nextTick(() => {
                        const tooltipTriggerList = document.querySelectorAll('#createTouchpointModal [data-bs-toggle="tooltip"]');
                        [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                            const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                            if (existingTooltip) {
                                existingTooltip.dispose();
                            }
                            new window.bootstrap.Tooltip(tooltipTriggerEl);
                        });
                    });
                });
            }
        }
    },
    mounted() {
        this.setupModalEventListeners();
    }
});
</script>

